import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Heart, Sun, Zap, ChevronDown, Truck, ShieldCheck, RotateCcw, Headphones, RotateCcw as ResetIcon, X, Check, Eye } from 'lucide-react';
import { fetchProductsFromBackend, fetchWishlistFromBackend, toggleWishlistBackend, getProductImageUrl } from '../services/api';

const Shop = ({ cartItems = [], onAddToCart, onUpdateQuantity, onBuyNow }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All Panels");
  const [selectedPowerRanges, setSelectedPowerRanges] = useState([]);
  const [selectedWavelengths, setSelectedWavelengths] = useState([]);
  const [sortBy, setSortBy] = useState("featured");
  const [showCount, setShowCount] = useState(12);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isShowOpen, setIsShowOpen] = useState(false);
  const [activeWishlist, setActiveWishlist] = useState({});
  const [quickViewProduct, setQuickViewProduct] = useState(null);

  const sortOptions = [
    { value: 'featured', label: 'Featured' },
    { value: 'price-low', label: 'Price Low to High' },
    { value: 'price-high', label: 'Price High to Low' },
    { value: 'popular', label: 'Most Popular' },
  ];

  useEffect(() => {
    let isMounted = true;
    const loadProducts = async () => {
      setLoading(true);
      try {
        const backendItems = await fetchProductsFromBackend();
        if (isMounted) {
          if (backendItems && Array.isArray(backendItems) && backendItems.length > 0) {
            const merged = backendItems.map((p, idx) => {
              const w = parseInt(p.actual_power) || 120;
              let cat = "Mid Size Panels";
              if (w <= 150) cat = "Mini Panels";
              else if (w > 150 && w <= 300) cat = "Mid Size Panels";
              else if (w > 300 && w <= 400) cat = "Pro Panels";
              else cat = "Max Panels";

              return {
                ...p,
                wattage: w,
                actual_power: p.actual_power || `${w}W`,
                category: cat,
                spectrum: p.spectrum || "660nm Red Light & 850nm Near-Infrared",
                isPopular: idx === 2
              };
            });
            setProducts(merged);
          } else {
            setProducts([]);
          }
        }
      } catch (err) {
        if (isMounted) setProducts([]);
      } finally {
        if (isMounted) setLoading(false);
      }

      // Fetch Live User Wishlist
      try {
        const userWishlist = await fetchWishlistFromBackend();
        if (isMounted && Array.isArray(userWishlist)) {
          const wishlistMap = {};
          userWishlist.forEach(w => {
            wishlistMap[w.id] = true;
          });
          setActiveWishlist(wishlistMap);
        }
      } catch (e) {}
    };
    loadProducts();
    return () => { isMounted = false; };
  }, []);

  const toggleWishlist = async (id) => {
    setActiveWishlist((prev) => ({ ...prev, [id]: !prev[id] }));
    await toggleWishlistBackend(id);
  };

  const handlePowerChange = (range) => {
    setSelectedPowerRanges(prev =>
      prev.includes(range) ? prev.filter(r => r !== range) : [...prev, range]
    );
  };

  const handleWavelengthChange = (wave) => {
    setSelectedWavelengths(prev =>
      prev.includes(wave) ? prev.filter(w => w !== wave) : [...prev, wave]
    );
  };

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      if (selectedCategory !== "All Panels" && p.category !== selectedCategory) {
        return false;
      }
      if (selectedPowerRanges.length > 0) {
        const w = p.wattage || 120;
        const matchesPower = selectedPowerRanges.some(range => {
          if (range === "100W-200W") return w >= 100 && w <= 200;
          if (range === "200W-400W") return w > 200 && w <= 400;
          if (range === "400W+") return w > 400;
          return true;
        });
        if (!matchesPower) return false;
      }
      // 3. Wavelength Filter (Robust Multi-Field Search)
      if (selectedWavelengths.length > 0) {
        const fullProductText = `${p.spectrum || ''} ${p.name || ''} ${p.description || ''}`.toLowerCase();
        
        // Every checked wavelength requirement must be satisfied by the product
        const matchesAllChecked = selectedWavelengths.every(wave => {
          if (wave === "660nm") {
            return fullProductText.includes("660") || fullProductText.includes("red");
          }
          if (wave === "850nm") {
            return fullProductText.includes("850") || fullProductText.includes("nir") || fullProductText.includes("infrared");
          }
          return fullProductText.includes(wave.toLowerCase());
        });

        if (!matchesAllChecked) return false;
      }
      return true;
    }).sort((a, b) => {
      if (sortBy === "price-low") return a.price - b.price;
      if (sortBy === "price-high") return b.price - a.price;
      if (sortBy === "popular") return (b.isPopular ? 1 : 0) - (a.isPopular ? 1 : 0);
      return 0;
    });
  }, [products, selectedCategory, selectedPowerRanges, selectedWavelengths, sortBy]);

  const categories = useMemo(() => [
    { name: "All Panels", count: products.length },
    { name: "Mini Panels", count: products.filter(p => p.category === "Mini Panels").length },
    { name: "Mid Size Panels", count: products.filter(p => p.category === "Mid Size Panels").length },
    { name: "Pro Panels", count: products.filter(p => p.category === "Pro Panels").length },
    { name: "Max Panels", count: products.filter(p => p.category === "Max Panels").length },
  ], [products]);

  const resetAllFilters = () => {
    setSelectedCategory("All Panels");
    setSelectedPowerRanges([]);
    setSelectedWavelengths([]);
  };

  return (
    <div className="shop-page-container" style={{ background: '#ffffff', minHeight: '100vh', padding: '32px 56px 80px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div style={{ maxWidth: '1360px', margin: '0 auto' }}>
        
        {/* Breadcrumb & Header Title */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '8px' }}>
            <Link to="/" style={{ color: '#94a3b8', textDecoration: 'none' }}>Home</Link>
            <span style={{ margin: '0 8px' }}>&gt;</span>
            <span style={{ color: '#0f172a', fontWeight: 600 }}>Shop All</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h1 style={{ fontSize: '38px', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em', margin: 0 }}>
                Our Red Light Therapy <span style={{ color: '#dc2626' }}>Panels</span>
              </h1>
              <p style={{ fontSize: '15px', color: '#64748b', marginTop: '6px' }}>
                Showing <strong>{filteredProducts.length}</strong> results. Powerful. Reliable. Built for real results.
              </p>
            </div>

            {/* Sort & Filter Controls (Custom Designed Dropdowns matching products page ui.png) */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', position: 'relative', flexWrap: 'wrap' }}>
              
              {/* Custom Sort By Dropdown */}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => { setIsSortOpen(!isSortOpen); setIsShowOpen(false); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    border: isSortOpen ? '1px solid #dc2626' : '1px solid #e2e8f0',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    fontSize: '13.5px',
                    color: '#0f172a',
                    fontWeight: 700,
                    background: '#ffffff',
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                  }}
                >
                  <span>Sort by: <strong style={{ color: '#dc2626' }}>{sortOptions.find(o => o.value === sortBy)?.label}</strong></span>
                  <ChevronDown size={14} color="#64748b" style={{ transform: isSortOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
                </button>

                {isSortOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      right: 0,
                      width: '210px',
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: '6px',
                      boxShadow: '0 12px 28px -6px rgba(15, 23, 42, 0.15)',
                      zIndex: 100
                    }}
                  >
                    {sortOptions.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => { setSortBy(opt.value); setIsSortOpen(false); }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 12px',
                          fontSize: '13px',
                          fontWeight: sortBy === opt.value ? 800 : 600,
                          color: sortBy === opt.value ? '#dc2626' : '#334155',
                          background: sortBy === opt.value ? '#fef2f2' : 'transparent',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                      >
                        <span>{opt.label}</span>
                        {sortBy === opt.value && <Check size={14} color="#dc2626" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {(selectedCategory !== "All Panels" || selectedPowerRanges.length > 0 || selectedWavelengths.length > 0) && (
                <button
                  onClick={resetAllFilters}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '9px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
                >
                  <ResetIcon size={14} />
                  <span>Reset Filters</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Storefront Body: Left Sidebar + COMPACT 3-CARD PRODUCTS GRID */}
        <div className="shop-main-layout">
          
          {/* LEFT SIDEBAR FILTERS */}
          <div className="shop-sidebar-filters">
            
            {/* Categories Box */}
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', background: '#ffffff' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <span style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '0.1em', color: '#0f172a' }}>CATEGORIES</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {categories.map((cat) => {
                  const isActive = selectedCategory === cat.name;
                  return (
                    <button
                      key={cat.name}
                      onClick={() => setSelectedCategory(cat.name)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: 'none',
                        background: isActive ? '#fef2f2' : 'transparent',
                        color: isActive ? '#dc2626' : '#475569',
                        fontWeight: isActive ? 700 : 600,
                        fontSize: '13.5px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <span>{cat.name}</span>
                      <span style={{ fontSize: '11px', background: isActive ? '#fecaca' : '#f1f5f9', color: isActive ? '#dc2626' : '#64748b', padding: '2px 8px', borderRadius: '9999px' }}>
                        {cat.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active Filters Box */}
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', background: '#ffffff' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <span style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '0.1em', color: '#0f172a' }}>FILTERS</span>
              </div>

              {/* WAVELENGTH */}
              <div style={{ marginBottom: '20px' }}>
                <span style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', letterSpacing: '0.08em', display: 'block', marginBottom: '10px' }}>
                  WAVELENGTH
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px', color: '#475569' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedWavelengths.includes("660nm")}
                      onChange={() => handleWavelengthChange("660nm")}
                      style={{ accentColor: '#dc2626' }}
                    />
                    <span>660nm Red Light</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedWavelengths.includes("850nm")}
                      onChange={() => handleWavelengthChange("850nm")}
                      style={{ accentColor: '#dc2626' }}
                    />
                    <span>850nm Near-Infrared</span>
                  </label>
                </div>
              </div>

              {/* POWER RANGES */}
              <div>
                <span style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', letterSpacing: '0.08em', display: 'block', marginBottom: '10px' }}>
                  POWER OUTPUT
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px', color: '#475569' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedPowerRanges.includes("100W-200W")}
                      onChange={() => handlePowerChange("100W-200W")}
                      style={{ accentColor: '#dc2626' }}
                    />
                    <span>100W - 200W</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedPowerRanges.includes("200W-400W")}
                      onChange={() => handlePowerChange("200W-400W")}
                      style={{ accentColor: '#dc2626' }}
                    />
                    <span>200W - 400W</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedPowerRanges.includes("400W+")}
                      onChange={() => handlePowerChange("400W+")}
                      style={{ accentColor: '#dc2626' }}
                    />
                    <span>400W+</span>
                  </label>
                </div>
              </div>

            </div>

          </div>

          {/* RIGHT PRODUCTS GRID (COMPACT UNIFORM CARDS, 3 PER ROW) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            
            {loading ? (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '64px', textAlign: 'center' }}>
                <div style={{ width: '36px', height: '36px', border: '3px solid #fecaca', borderTopColor: '#dc2626', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 1s linear infinite' }} />
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>Loading panels...</h3>
                <p style={{ fontSize: '14px', color: '#64748b' }}>Fetching red light therapy panels from our collection.</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '48px', textAlign: 'center' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>No matching panels found</h3>
                <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>Try resetting your filters or selecting a different category.</p>
                <button onClick={resetAllFilters} className="btn-red-pill">Reset All Filters</button>
              </div>
            ) : (
              <div className="shop-products-cards-grid">
                {filteredProducts.map((p) => {
                  const isPopular = p.isPopular;
                  const isLiked = activeWishlist[p.id];
                  const priceNum = Number(p.price || 350);
                  const salePriceNum = p.sale_price ? Number(p.sale_price) : null;

                  return (
                    <div
                      key={p.id}
                      style={{
                        background: '#ffffff',
                        border: isPopular ? '2px solid #dc2626' : '1px solid #e2e8f0',
                        borderRadius: '16px',
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        boxShadow: isPopular ? '0 12px 24px -6px rgba(220, 38, 38, 0.12)' : '0 2px 8px rgba(0,0,0,0.02)'
                      }}
                    >
                      {/* MOST POPULAR Red Header Pill */}
                      {isPopular && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '-12px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: '#dc2626',
                            color: '#ffffff',
                            fontSize: '10px',
                            fontWeight: 900,
                            letterSpacing: '0.1em',
                            padding: '3px 14px',
                            borderRadius: '9999px',
                            textTransform: 'uppercase',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          MOST POPULAR
                        </div>
                      )}

                      {/* Top Row: LED count badge + Wishlist heart */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 800, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', padding: '3px 12px', borderRadius: '9999px' }}>
                          {p.led_chips_count} LEDs
                        </span>

                        <button
                          onClick={() => toggleWishlist(p.id)}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: isLiked ? '#dc2626' : '#94a3b8' }}
                        >
                          <Heart size={18} fill={isLiked ? "#dc2626" : "none"} />
                        </button>
                      </div>
                      {/* Product Photo Render */}
                      <Link to={`/product/${p.id}`} style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '18px', textDecoration: 'none' }}>
                        <img
                          src={p.image_url}
                          alt={p.name}
                          style={{ maxHeight: '200px', maxWidth: '100%', objectFit: 'contain', display: 'block' }}
                          onError={(e) => { e.target.src = getProductImageUrl('/uploads/panel_72led.jpg'); }}
                        />
                      </Link>

                      {/* Title */}
                      <Link to={`/product/${p.id}`} style={{ textDecoration: 'none' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a', marginBottom: '8px', lineHeight: 1.3 }}>
                          {p.name}
                        </h3>
                      </Link>

                      {/* Spec Badges Row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', fontSize: '12.5px', color: '#64748b', marginBottom: '14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Sun size={14} color="#dc2626" />
                          <span>660nm & 850nm</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Zap size={14} color="#dc2626" />
                          <span>{p.actual_power}</span>
                        </div>
                      </div>

                      {/* Dedicated Price & Stock Row (Right Aligned Price) */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px', marginTop: 'auto' }}>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: '#16a34a', background: '#dcfce7', padding: '3px 10px', borderRadius: '9999px' }}>
                          In Stock
                        </span>

                        <div style={{ textAlign: 'right', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                          <span style={{ fontSize: '19px', fontWeight: 900, color: '#dc2626', lineHeight: 1 }}>
                            £{(salePriceNum && salePriceNum < priceNum ? salePriceNum : priceNum).toFixed(2)}
                          </span>
                          {salePriceNum && salePriceNum < priceNum && (
                            <span style={{ fontSize: '13px', color: '#94a3b8', textDecoration: 'line-through' }}>
                              £{priceNum.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* CTA Buttons: Buy Now + Add to Cart OR Quantity (+ -) Controls */}
                      <div style={{ marginTop: '4px' }}>
                        {(() => {
                          const cartItem = cartItems.find((item) => Number(item.id) === Number(p.id) || Number(item.product_id) === Number(p.id));
                          const currentQty = cartItem ? cartItem.quantity : 0;
                          const availableStock = p.stock !== undefined ? p.stock : 25;

                          if (currentQty > 0) {
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '6px 12px', width: '100%' }}>
                                <button
                                  onClick={() => onUpdateQuantity && onUpdateQuantity(p.id, -1, availableStock)}
                                  style={{ width: '28px', height: '28px', borderRadius: '6px', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', fontWeight: 900, fontSize: '15px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                  -
                                </button>
                                <span style={{ fontSize: '13px', fontWeight: 900, color: '#dc2626' }}>
                                  {currentQty} in Cart
                                </span>
                                <button
                                  onClick={() => onUpdateQuantity && onUpdateQuantity(p.id, 1, availableStock)}
                                  disabled={currentQty >= availableStock}
                                  style={{ width: '28px', height: '28px', borderRadius: '6px', background: currentQty >= availableStock ? '#f1f5f9' : '#dc2626', border: 'none', color: currentQty >= availableStock ? '#94a3b8' : '#ffffff', fontWeight: 900, fontSize: '15px', cursor: currentQty >= availableStock ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                  +
                                </button>
                              </div>
                            );
                          }

                          return (
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => onBuyNow && onBuyNow(p)}
                                style={{
                                  flex: 1,
                                  background: '#dc2626',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '11px 8px',
                                  fontSize: '13px',
                                  fontWeight: 800,
                                  cursor: 'pointer',
                                  textAlign: 'center'
                                }}
                              >
                                Buy Now
                              </button>

                              <button
                                onClick={() => onAddToCart && onAddToCart(p)}
                                style={{
                                  flex: 1,
                                  background: '#ffffff',
                                  color: '#0f172a',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '8px',
                                  padding: '11px 8px',
                                  fontSize: '13px',
                                  fontWeight: 800,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px'
                                }}
                              >
                                <ShoppingBag size={15} />
                                <span>Add to Cart</span>
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

           

          </div>

        </div>

      </div>

      {/* FULL SCREEN / QUICK VIEW PRODUCT DETAIL MODAL */}
      {quickViewProduct && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ background: '#ffffff', borderRadius: '24px', maxWidth: '840px', width: '100%', padding: '36px', position: 'relative', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
            <button
              onClick={() => setQuickViewProduct(null)}
              style={{ position: 'absolute', top: '20px', right: '20px', background: '#f1f5f9', border: 'none', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#0f172a' }}
            >
              <X size={20} />
            </button>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '36px', alignItems: 'center' }}>
              <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '16px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                <img
                  src={quickViewProduct.image_url}
                  alt={quickViewProduct.name}
                  style={{ maxHeight: '320px', maxWidth: '100%', objectFit: 'contain' }}
                />
              </div>

              <div>
                <span style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '0.15em', color: '#dc2626', textTransform: 'uppercase' }}>
                  FULL PRODUCT VIEW • {quickViewProduct.sku || 'IE-PANEL'}
                </span>
                <h2 style={{ fontSize: '28px', fontWeight: 900, color: '#0f172a', margin: '6px 0 12px' }}>
                  {quickViewProduct.name}
                </h2>
                <div style={{ fontSize: '28px', fontWeight: 900, color: '#dc2626', marginBottom: '16px' }}>
                  £{Number(quickViewProduct.price).toFixed(2)}
                </div>

                <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.6, marginBottom: '20px' }}>
                  {quickViewProduct.description}
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '28px', fontSize: '13px', color: '#334155' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#dc2626" /><span><strong>LED Chips:</strong> {quickViewProduct.led_chips_count} Dual-Chip LEDs</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#dc2626" /><span><strong>Actual Power:</strong> {quickViewProduct.actual_power}</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#dc2626" /><span><strong>Wavelengths:</strong> {quickViewProduct.spectrum}</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#dc2626" /><span><strong>Warranty:</strong> 2 Year Full Coverage</span></div>
                </div>

                <button
                  className="btn-red-pill"
                  style={{ width: '100%', padding: '14px', justifyContent: 'center' }}
                  onClick={() => {
                    onAddToCart && onAddToCart(quickViewProduct);
                    setQuickViewProduct(null);
                  }}
                >
                  <ShoppingBag size={18} />
                  <span>ADD TO CART NOW</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Shop;
